import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Zap, 
  Building2, 
  Euro,
  FileText,
  Edit,
  Trash2,
  Receipt,
  Download,
  Eye
} from "lucide-react";
import ClientForm from "./ClientForm";
import DocumentManager from "./DocumentManager";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDetails = ({ client, onClose, onUpdate, onDelete }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [clientContracts, setClientContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [clientDevisFactures, setClientDevisFactures] = useState([]);
  const [loadingDevisFactures, setLoadingDevisFactures] = useState(false);
  const [clientWithCreatorInfo, setClientWithCreatorInfo] = useState(client);
  
  // États pour les assignations Telepro/Commercial
  const [telepros, setTelepros] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [isEditingAssignments, setIsEditingAssignments] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    telepro_id: client.created_by || '',
    commercial_id: client.assigned_commercial || ''
  });
  
  // Nouveau contrat
  const [showCreateContractDialog, setShowCreateContractDialog] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [newContract, setNewContract] = useState({
    supplier_id: '',
    contract_type: 'electricity',
    pdl: '',
    adresse_site: '',
    code_postal_site: '',
    ville_site: '',
    start_date: '',
    end_date: '',
    power_subscribed: 0,
    annual_consumption: 0,
    unit_price: 0,
    annual_cost: 0,
    commission_rate: 0.05,
    status: 'pending'
  });
  
  // Trésorerie contrat
  const [showContractTreasuryModal, setShowContractTreasuryModal] = useState(false);
  const [selectedContractForTreasury, setSelectedContractForTreasury] = useState(null);
  const [contractTreasuryData, setContractTreasuryData] = useState({
    montant_avance: 0,
    date_reglement_avance: '',
    avance_status: 'pending',
    montant_deuxieme_tranche: 0,
    date_reglement_deuxieme_tranche: '',
    deuxieme_tranche_status: 'pending',
    paiement_mensuel: 0,
    duree_mois: 12,
    date_debut_mensuel: '',
    avances_supplementaires: []
  });

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[ClientDetails] Current user loaded:', response.data);
      console.log('[ClientDetails] is_super_admin:', response.data.is_super_admin);
      console.log('[ClientDetails] role:', response.data.role);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  // Helper pour vérifier si l'utilisateur a les droits admin
  const isAdminUser = () => {
    if (!currentUser) {
      console.log('[ClientDetails] isAdminUser: currentUser is null');
      return false;
    }
    const result = currentUser.is_super_admin || currentUser.role === 'admin' || currentUser.role === 'administratif';
    console.log('[ClientDetails] isAdminUser:', result, 'is_super_admin:', currentUser.is_super_admin, 'role:', currentUser.role);
    return result;
  };

  // Function to refresh client info
  const fetchClientInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${client.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientWithCreatorInfo(response.data);
    } catch (error) {
      console.error("Error fetching client details:", error);
      // Fallback to original client data
      setClientWithCreatorInfo(client);
    }
  };

  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/clients/${client.id}/details`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClientWithCreatorInfo(response.data);
      } catch (error) {
        console.error("Error fetching client details:", error);
        // Fallback to original client data
        setClientWithCreatorInfo(client);
      }
    };

    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const teleprosData = response.data.filter(user => user.role === 'telepro');
        const commerciauxData = response.data.filter(user => 
          user.role === 'commercial' || user.role === 'regie'
        );
        
        setTelepros(teleprosData);
        setCommerciaux(commerciauxData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchClientDetails();
    fetchUsers();
  }, [client.id]);

  useEffect(() => {
    if (activeTab === 'contracts') {
      fetchClientContracts();
      fetchSuppliers();
    } else if (activeTab === 'devis-factures') {
      fetchClientDevisFactures();
    }
  }, [activeTab, client.id]);

  // Fonctions pour gérer les assignations
  const handleSaveAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Préparer les données de mise à jour
      const updateData = {
        ...clientWithCreatorInfo,
        created_by: assignmentData.telepro_id,
        assigned_commercial: assignmentData.commercial_id
      };

      // Mettre à jour via l'API
      await axios.put(`${API}/clients/${client.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mettre à jour l'état local
      const telepro = telepros.find(t => t.id === assignmentData.telepro_id);
      const commercial = commerciaux.find(c => c.id === assignmentData.commercial_id);
      
      setClientWithCreatorInfo({
        ...clientWithCreatorInfo,
        created_by: assignmentData.telepro_id,
        assigned_commercial: assignmentData.commercial_id,
        telepro_name: telepro ? `${telepro.first_name} ${telepro.last_name}` : '',
        commercial_name: commercial ? `${commercial.first_name} ${commercial.last_name}` : ''
      });

      setIsEditingAssignments(false);
      toast.success("Assignations mises à jour avec succès");
      
      // Rafraîchir les données du parent
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error("Error updating assignments:", error);
      toast.error("Erreur lors de la mise à jour des assignations");
    }
  };

  const handleCancelAssignments = () => {
    setAssignmentData({
      telepro_id: clientWithCreatorInfo.created_by || '',
      commercial_id: clientWithCreatorInfo.assigned_commercial || ''
    });
    setIsEditingAssignments(false);
  };

  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/config/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching config suppliers:", error);
      toast.error("Erreur lors du chargement des fournisseurs");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchClientContracts = async () => {
    setLoadingContracts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contracts/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter contracts for this client and add creator information
      const clientContractsWithCreator = response.data.filter(
        contract => contract.client_id === client.id
      );
      
      setClientContracts(clientContractsWithCreator);
    } catch (error) {
      console.error("Error fetching client contracts:", error);
      toast.error("Erreur lors du chargement des contrats");
    } finally {
      setLoadingContracts(false);
    }
  };

  const fetchClientDevisFactures = async () => {
    setLoadingDevisFactures(true);
    try {
      const response = await axios.get(`${API}/clients/${client.id}/devis-factures`);
      setClientDevisFactures(response.data);
    } catch (error) {
      console.error("Error fetching client devis/factures:", error);
      toast.error("Erreur lors du chargement des devis et factures");
    } finally {
      setLoadingDevisFactures(false);
    }
  };

  const handleCreateContract = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Find supplier details
      const selectedSupplier = suppliers.find(s => s.id === newContract.supplier_id);
      if (!selectedSupplier) {
        toast.error("Veuillez sélectionner un fournisseur");
        return;
      }

      const contractData = {
        ...newContract,
        supplier_name: selectedSupplier.name, // Use supplier name from selection
        client_id: client.id,
        annual_cost: parseFloat(newContract.annual_cost),
        power_subscribed: parseFloat(newContract.power_subscribed),
        annual_consumption: parseFloat(newContract.annual_consumption),
        unit_price: parseFloat(newContract.unit_price),
        commission_rate: parseFloat(newContract.commission_rate)
      };

      await axios.post(`${API}/contracts`, contractData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Contrat créé avec succès");
      setShowCreateContractDialog(false);
      setNewContract({
        supplier_id: '',
        contract_type: 'electricity',
        pdl: '',
        adresse_site: '',
        code_postal_site: '',
        ville_site: '',
        start_date: '',
        end_date: '',
        power_subscribed: 0,
        annual_consumption: 0,
        unit_price: 0,
        annual_cost: 0,
        commission_rate: 0.05,
        status: 'pending'
      });
      
      // Refresh contracts list
      fetchClientContracts();
    } catch (error) {
      console.error("Error creating contract:", error);
      toast.error("Erreur lors de la création du contrat");
    }
  };

  const handleOpenContractTreasuryModal = async (contract) => {
    setSelectedContractForTreasury(contract);
    
    try {
      // Récupérer les données de trésorerie existantes pour ce contrat
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/contracts/${contract.id}/treasury`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setContractTreasuryData(response.data || {
        montant_avance: contract.montant_avance || 0,
        paiement_mensuel: contract.paiement_mensuel || 0,
        avances_supplementaires: contract.avances_supplementaires || []
      });
    } catch (error) {
      console.error("Error fetching contract treasury data:", error);
      // Utiliser les données du contrat ou valeurs par défaut
      setContractTreasuryData({
        montant_avance: contract.montant_avance || 0,
        paiement_mensuel: contract.paiement_mensuel || 0,
        avances_supplementaires: contract.avances_supplementaires || []
      });
    }
    
    setShowContractTreasuryModal(true);
  };

  const handleSaveClient = async (clientData, documents) => {
    try {
      const token = localStorage.getItem('token');
      
      // Update client data
      await axios.put(`${API}/clients/${client.id}`, clientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload documents if any
      if (documents && Object.values(documents).some(doc => doc)) {
        for (const [docType, file] of Object.entries(documents)) {
          if (file) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('file_type', docType);
            
            await axios.post(`${API}/clients/${client.id}/documents`, formData, {
              headers: { 
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
              }
            });
          }
        }
      }
      
      // Rafraîchir les informations du client
      await fetchClientInfo();
      
      toast.success("Client mis à jour avec succès");
      setShowEditDialog(false);
      onUpdate();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Erreur lors de la mise à jour: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteClient = async () => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce client ? Tous les documents associés seront également supprimés.")) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${client.id}`);
      toast.success("Client supprimé avec succès");
      onDelete();
      onClose();
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      nouveau: "bg-blue-100 text-blue-800",
      en_cours: "bg-amber-100 text-amber-800",
      signe: "bg-emerald-100 text-emerald-800",
      refuse: "bg-red-100 text-red-800",
      attente: "bg-purple-100 text-purple-800"
    };
    return colors[status] || colors.nouveau;
  };

  const getSolvabilityColor = (note) => {
    const colors = {
      excellent: "bg-emerald-100 text-emerald-800",
      bon: "bg-blue-100 text-blue-800",
      moyen: "bg-amber-100 text-amber-800",
      faible: "bg-red-100 text-red-800"
    };
    return colors[note] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Non définie";
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {client.prenom} {client.nom}
            </h2>
            <p className="text-gray-600">{client.company_name}</p>
            <div className="flex items-center space-x-2 mt-1">
              <Badge className={getStatusColor(client.etat_dossier)}>
                {client.etat_dossier}
              </Badge>
              {client.note_solvabilite && (
                <Badge className={getSolvabilityColor(client.note_solvabilite)}>
                  Solvabilité: {client.note_solvabilite}
                </Badge>
              )}
              {clientWithCreatorInfo.assigned_commercial_name && (
                <Badge variant="outline" className="text-blue-700 border-blue-300 bg-blue-50">
                  <User className="w-3 h-3 mr-1" />
                  Commercial: {clientWithCreatorInfo.assigned_commercial_name}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => setShowEditDialog(true)}
            data-testid="edit-client-btn"
          >
            <Edit className="w-4 h-4 mr-2" />
            Modifier
          </Button>
          <Button
            variant="outline"
            onClick={handleDeleteClient}
            className="text-red-600 hover:text-red-700"
            data-testid="delete-client-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="energy">Énergie</TabsTrigger>
          <TabsTrigger value="contracts">Contrats</TabsTrigger>
          <TabsTrigger value="devis-factures">Devis & Factures</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* General Information */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Phone className="w-5 h-5" />
                  <span>Contact</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{client.email}</span>
                </div>
                {client.telephone_portable && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{client.telephone_portable}</span>
                  </div>
                )}
                {client.telephone_fixe && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{client.telephone_fixe} (Fixe)</span>
                  </div>
                )}
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                  <div>
                    <div>{client.adresse}</div>
                    <div>{client.code_postal} {client.ville}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assignations Telepro/Commercial */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Assignations</span>
                  </div>
                  {isEditingAssignments ? (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCancelAssignments}
                      >
                        Annuler
                      </Button>
                      <Button 
                        size="sm"
                        onClick={handleSaveAssignments}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Sauvegarder
                      </Button>
                    </div>
                  ) : (
                    isAdminUser() && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsEditingAssignments(true)}
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Modifier
                      </Button>
                    )
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Telepro Assignment */}
                <div>
                  <span className="text-sm text-gray-500 block mb-1">Telepro:</span>
                  {!isEditingAssignments ? (
                    <div className="font-medium">
                      {clientWithCreatorInfo.telepro_name || 
                       clientWithCreatorInfo.created_by_name || 
                       "Non assigné"}
                    </div>
                  ) : (
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={assignmentData.telepro_id}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData,
                        telepro_id: e.target.value
                      })}
                    >
                      <option value="">Sélectionner un Telepro</option>
                      {telepros.map(telepro => (
                        <option key={telepro.id} value={telepro.id}>
                          {telepro.first_name} {telepro.last_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Commercial Assignment */}
                <div>
                  <span className="text-sm text-gray-500 block mb-1">Commercial/Régie:</span>
                  {!isEditingAssignments ? (
                    <div className="font-medium">
                      {clientWithCreatorInfo.commercial_name || 
                       clientWithCreatorInfo.assigned_commercial_name || 
                       "Non assigné"}
                    </div>
                  ) : (
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      value={assignmentData.commercial_id}
                      onChange={(e) => setAssignmentData({
                        ...assignmentData,
                        commercial_id: e.target.value
                      })}
                    >
                      <option value="">Sélectionner un Commercial/Régie</option>
                      {commerciaux.map(commercial => (
                        <option key={commercial.id} value={commercial.id}>
                          {commercial.first_name} {commercial.last_name} ({commercial.role})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Company Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5" />
                  <span>Entreprise</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">SIREN:</span>
                  <div className="font-medium">{client.siren || "Non renseigné"}</div>
                </div>
                {client.apporteur && (
                  <div>
                    <span className="text-sm text-gray-500">Apporteur:</span>
                    <div className="font-medium">{client.apporteur}</div>
                  </div>
                )}
                <div>
                  <span className="text-sm text-gray-500">Téléprospection:</span>
                  <div className="font-medium">{client.telepro ? "Oui" : "Non"}</div>
                </div>
              </CardContent>
            </Card>

            {/* Important Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>Dates Importantes</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-sm text-gray-500">RDV:</span>
                  <div className="font-medium">{formatDate(client.date_rdv)}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">R2:</span>
                  <div className="font-medium">{formatDate(client.date_r2)}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Signature:</span>
                  <div className="font-medium">{formatDate(client.date_signature)}</div>
                </div>
              </CardContent>
            </Card>

            {/* Commercial Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Euro className="w-5 h-5" />
                  <span>Données Commerciales</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.commission && (
                  <div>
                    <span className="text-sm text-gray-500">Commission:</span>
                    <div className="font-medium">{client.commission}€</div>
                  </div>
                )}
                <div>
                  <span className="text-sm text-gray-500">Formule de paiement:</span>
                  <div className="font-medium">{client.formule_paiement}</div>
                </div>
                {client.nouveau_fournisseur && (
                  <div>
                    <span className="text-sm text-gray-500">Nouveau fournisseur:</span>
                    <div className="font-medium">{client.nouveau_fournisseur}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Comments */}
          {client.commentaires && (
            <Card>
              <CardHeader>
                <CardTitle>Commentaires</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{client.commentaires}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Energy Information */}
        <TabsContent value="energy" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Electricity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  <span>Électricité</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Fournisseur actuel:</span>
                  <div className="font-medium">{client.fournisseur_elec_actuel || "Non renseigné"}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Échéance:</span>
                  <div className="font-medium">{formatDate(client.echeance_elec)}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Puissance souscrite:</span>
                  <div className="font-medium">{client.puissance_souscrite} kVA</div>
                </div>
                {client.pdl_elec && (
                  <div>
                    <span className="text-sm text-gray-500">PDL:</span>
                    <div className="font-medium font-mono text-sm">{client.pdl_elec}</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <span>Gaz</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Fournisseur actuel:</span>
                  <div className="font-medium">{client.fournisseur_gaz_actuel || "Non renseigné"}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Échéance:</span>
                  <div className="font-medium">{formatDate(client.echeance_gaz)}</div>
                </div>
                {client.profil_gaz && (
                  <div>
                    <span className="text-sm text-gray-500">Profil:</span>
                    <div className="font-medium">{client.profil_gaz}</div>
                  </div>
                )}
                {client.pdl_gaz && (
                  <div>
                    <span className="text-sm text-gray-500">PDL:</span>
                    <div className="font-medium font-mono text-sm">{client.pdl_gaz}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contract Dates */}
          {(client.debut_contrat || client.fin_contrat) && (
            <Card>
              <CardHeader>
                <CardTitle>Nouveau Contrat</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div>
                  <span className="text-sm text-gray-500">Début de contrat:</span>
                  <div className="font-medium">{formatDate(client.debut_contrat)}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Fin de contrat:</span>
                  <div className="font-medium">{formatDate(client.fin_contrat)}</div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Contracts Information */}
        <TabsContent value="contracts" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Contrats ({clientContracts.length})</h3>
            <Button 
              onClick={() => setShowCreateContractDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Nouveau Contrat
            </Button>
          </div>
          
          {loadingContracts ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : clientContracts.length > 0 ? (
            <div className="space-y-4">
              {clientContracts.map((contract) => (
                <Card key={contract.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          {contract.contract_type === 'electricity' ? (
                            <Zap className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Building2 className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {contract.supplier_name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Contrat {contract.contract_type === 'electricity' ? 'électricité' : 'gaz'}
                          </p>
                          {/* PDL en évidence */}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                              PDL: {contract.pdl || 'Non renseigné'}
                            </Badge>
                            {contract.created_by_name && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                                <User className="w-3 h-3 mr-1" />
                                Créé par: {contract.created_by_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={
                          contract.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                          contract.status === 'expired' ? 'bg-red-100 text-red-800' :
                          contract.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {contract.status === 'active' ? 'Actif' :
                           contract.status === 'expired' ? 'Expiré' :
                           contract.status === 'pending' ? 'En attente' : 'Annulé'}
                        </Badge>
                      </div>
                    </div>

                    {/* Adresse de consommation en évidence */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center mb-1">
                        <MapPin className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Adresse de consommation :</span>
                      </div>
                      <p className="text-sm text-gray-900 ml-6">
                        {contract.adresse_site || 'Non renseignée'}
                        {contract.code_postal_site && contract.ville_site && (
                          <span>, {contract.code_postal_site} {contract.ville_site}</span>
                        )}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Début:</span>
                        <div className="font-medium">{new Date(contract.start_date).toLocaleDateString('fr-FR')}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Fin:</span>
                        <div className="font-medium">{new Date(contract.end_date).toLocaleDateString('fr-FR')}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Puissance:</span>
                        <div className="font-medium">{contract.power_subscribed} kVA</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Coût annuel:</span>
                        <div className="font-medium text-blue-600">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(contract.annual_cost)}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <span>Prix: {contract.unit_price.toFixed(3)} €/kWh</span>
                        <span>•</span>
                        <span>Commission: {(contract.commission_rate * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-green-600">
                          Commission: {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(contract.annual_cost * contract.commission_rate)}
                        </div>
                        {/* Bouton Gérer Trésorerie visible uniquement pour Super Admin et Admin/Administratif */}
                        {isAdminUser() && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => {
                              // Rediriger vers l'onglet Contrats avec ce contrat sélectionné
                              window.location.href = `/contracts?contract_id=${contract.id}&action=treasury`;
                            }}
                          >
                            <Euro className="w-4 h-4 mr-1" />
                            Gérer Trésorerie
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun contrat</h3>
                <p className="text-gray-500 mb-4">
                  Ce client n'a pas encore de contrat enregistré.
                </p>
                <Button 
                  onClick={() => setShowCreateContractDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Créer le premier contrat
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Devis & Factures Information */}
        <TabsContent value="devis-factures" className="space-y-6">
          {loadingDevisFactures ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : clientDevisFactures.length > 0 ? (
            <div className="space-y-4">
              {clientDevisFactures.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          {doc.type === 'devis' ? (
                            <FileText className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Receipt className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {doc.numero}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {doc.type === 'devis' ? 'Devis' : 'Facture'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={
                          doc.status === 'accepte' || doc.status === 'paye' ? 'bg-emerald-100 text-emerald-800' :
                          doc.status === 'refuse' ? 'bg-red-100 text-red-800' :
                          doc.status === 'envoye' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {doc.status === 'brouillon' ? 'Brouillon' :
                           doc.status === 'envoye' ? 'Envoyé' :
                           doc.status === 'accepte' ? 'Accepté' :
                           doc.status === 'refuse' ? 'Refusé' :
                           doc.status === 'paye' ? 'Payé' : doc.status}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Date création:</span>
                        <div className="font-medium">{new Date(doc.date_creation).toLocaleDateString('fr-FR')}</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Date échéance:</span>
                        <div className="font-medium">
                          {doc.date_echeance ? new Date(doc.date_echeance).toLocaleDateString('fr-FR') : 'N/A'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Articles:</span>
                        <div className="font-medium">{doc.items.length} article(s)</div>
                      </div>
                      <div>
                        <span className="text-gray-500">Montant TTC:</span>
                        <div className="font-medium text-blue-600">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR'
                          }).format(doc.total_ttc)}
                        </div>
                      </div>
                    </div>

                    {doc.items.length > 0 && (
                      <div className="mt-4">
                        <div className="text-sm font-medium text-gray-700 mb-2">Articles:</div>
                        <div className="space-y-1">
                          {doc.items.slice(0, 3).map((item, index) => (
                            <div key={index} className="flex justify-between text-sm text-gray-600">
                              <span>{item.libelle}</span>
                              <span>{item.quantite} x {item.prix_unitaire.toFixed(2)}€</span>
                            </div>
                          ))}
                          {doc.items.length > 3 && (
                            <div className="text-xs text-gray-500">
                              ... et {doc.items.length - 3} autre(s) article(s)
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <span>Sous-total HT: {doc.sous_total_ht.toFixed(2)}€</span>
                        <span>•</span>
                        <span>TVA: {doc.total_tva.toFixed(2)}€</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun devis ni facture</h3>
                <p className="text-gray-500">
                  Ce client n'a pas encore de devis ou facture enregistré.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Treasury Information */}
        {/* Documents */}
        <TabsContent value="documents">
          <DocumentManager 
            clientId={client.id} 
            clientName={`${client.prenom} ${client.nom}`}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Client</DialogTitle>
          </DialogHeader>
          <ClientForm
            client={client}
            onSave={handleSaveClient}
            onCancel={() => setShowEditDialog(false)}
            isEditing={true}
          />
        </DialogContent>
      </Dialog>

      {/* Create Contract Dialog */}
      <Dialog open={showCreateContractDialog} onOpenChange={setShowCreateContractDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Contrat - {client.company_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fournisseur d'énergie</Label>
                <Select 
                  value={newContract.supplier_id} 
                  onValueChange={(value) => setNewContract({...newContract, supplier_id: value})}
                  disabled={loadingSuppliers}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingSuppliers ? "Chargement..." : "Sélectionner un fournisseur"} />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                        <span className="text-xs text-gray-500 ml-2">
                          ({supplier.is_active ? 'Actif' : 'Inactif'})
                          {supplier.margin_rate && ` - Marge: ${supplier.margin_rate}%`}
                        </span>
                      </SelectItem>
                    ))}
                    {suppliers.length === 0 && (
                      <SelectItem value="" disabled>
                        Aucun fournisseur configuré
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Type de contrat</Label>
                <Select 
                  value={newContract.contract_type} 
                  onValueChange={(value) => setNewContract({...newContract, contract_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electricity">Électricité</SelectItem>
                    <SelectItem value="gas">Gaz</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Informations site/livraison - Section importante */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">📍 Informations du Site de Consommation</h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="font-medium text-blue-800">PDL (Point De Livraison) *</Label>
                  <Input
                    value={newContract.pdl}
                    onChange={(e) => setNewContract({...newContract, pdl: e.target.value})}
                    placeholder="ex: 09234567890123"
                    className="bg-white"
                  />
                  <p className="text-xs text-blue-600 mt-1">Identifiant unique du compteur (obligatoire)</p>
                </div>
                
                <div>
                  <Label className="font-medium text-blue-800">Adresse du site de consommation *</Label>
                  <Input
                    value={newContract.adresse_site}
                    onChange={(e) => setNewContract({...newContract, adresse_site: e.target.value})}
                    placeholder="Adresse complète du site"
                    className="bg-white"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-medium text-blue-800">Code postal</Label>
                    <Input
                      value={newContract.code_postal_site}
                      onChange={(e) => setNewContract({...newContract, code_postal_site: e.target.value})}
                      placeholder="69000"
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label className="font-medium text-blue-800">Ville</Label>
                    <Input
                      value={newContract.ville_site}
                      onChange={(e) => setNewContract({...newContract, ville_site: e.target.value})}
                      placeholder="Lyon"
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dates du contrat */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={newContract.start_date}
                  onChange={(e) => setNewContract({...newContract, start_date: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={newContract.end_date}
                  onChange={(e) => setNewContract({...newContract, end_date: e.target.value})}
                />
              </div>
            </div>

            {/* Informations techniques et financières */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Puissance souscrite (kVA)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newContract.power_subscribed}
                  onChange={(e) => setNewContract({...newContract, power_subscribed: e.target.value})}
                  placeholder="36"
                />
              </div>
              
              <div>
                <Label>Consommation annuelle (kWh)</Label>
                <Input
                  type="number"
                  value={newContract.annual_consumption}
                  onChange={(e) => setNewContract({...newContract, annual_consumption: e.target.value})}
                  placeholder="25000"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Prix unitaire (€/kWh)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={newContract.unit_price}
                  onChange={(e) => setNewContract({...newContract, unit_price: e.target.value})}
                  placeholder="0.156"
                />
              </div>
              
              <div>
                <Label>Coût annuel (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newContract.annual_cost}
                  onChange={(e) => setNewContract({...newContract, annual_cost: e.target.value})}
                  placeholder="3900"
                />
              </div>
              
              <div>
                <Label>Taux de commission (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={newContract.commission_rate}
                  onChange={(e) => setNewContract({...newContract, commission_rate: e.target.value})}
                  placeholder="0.05"
                />
              </div>
            </div>

            <div>
              <Label>Statut</Label>
              <Select 
                value={newContract.status} 
                onValueChange={(value) => setNewContract({...newContract, status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateContractDialog(false)}
              >
                Annuler
              </Button>
              <Button 
                onClick={handleCreateContract}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!newContract.supplier_id || !newContract.pdl || !newContract.adresse_site}
              >
                Créer le Contrat
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Treasury Management Modal */}
      <Dialog open={showContractTreasuryModal} onOpenChange={setShowContractTreasuryModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Gestion Trésorerie - {selectedContractForTreasury && clientWithCreatorInfo?.nom_entreprise}
            </DialogTitle>
            <div className="text-sm text-gray-600">
              {selectedContractForTreasury && (
                <>
                  Contrat {selectedContractForTreasury.supplier_name} • PDL: {selectedContractForTreasury.pdl} • 
                  Commission totale: {new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR'
                  }).format(selectedContractForTreasury.annual_cost * selectedContractForTreasury.commission_rate)}
                </>
              )}
            </div>
          </DialogHeader>
          
          {selectedContractForTreasury && (
            <div className="space-y-6">
              {/* Paiement d'avance initial */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">💰 Paiement d'Avance Initial</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-blue-800">Montant de l'avance (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={contractTreasuryData.montant_avance}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        montant_avance: parseFloat(e.target.value) || 0
                      })}
                      placeholder="0.00"
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600 mt-1">Paiement initial à l'avance</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-blue-800">Date de règlement</Label>
                    <Input
                      type="date"
                      value={contractTreasuryData.date_reglement_avance || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        date_reglement_avance: e.target.value
                      })}
                      className="bg-white"
                    />
                    <p className="text-xs text-blue-600 mt-1">Date de réception du paiement</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-blue-800">Status Avance</Label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                      value={contractTreasuryData.avance_status || 'pending'}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        avance_status: e.target.value
                      })}
                    >
                      <option value="pending">En attente</option>
                      <option value="received">Reçu</option>
                      <option value="partial">Partiel</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Deuxième tranche de règlement */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="text-sm font-semibold text-orange-900 mb-3">💰 Deuxième Tranche de Règlement</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-orange-800">Montant 2ème tranche (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={contractTreasuryData.montant_deuxieme_tranche || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        montant_deuxieme_tranche: parseFloat(e.target.value) || 0
                      })}
                      placeholder="0.00"
                      className="bg-white"
                    />
                    <p className="text-xs text-orange-600 mt-1">Paiement intermédiaire supplémentaire</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-orange-800">Date prévue de règlement</Label>
                    <Input
                      type="date"
                      value={contractTreasuryData.date_reglement_deuxieme_tranche || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        date_reglement_deuxieme_tranche: e.target.value
                      })}
                      className="bg-white"
                    />
                    <p className="text-xs text-orange-600 mt-1">Date prévue de réception</p>
                  </div>
                  
                  <div>
                    <Label className="font-medium text-orange-800">Status 2ème Tranche</Label>
                    <select 
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                      value={contractTreasuryData.deuxieme_tranche_status || 'pending'}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        deuxieme_tranche_status: e.target.value
                      })}
                    >
                      <option value="pending">En attente</option>
                      <option value="received">Reçu</option>
                      <option value="partial">Partiel</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Paiements mensuels */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-semibold text-green-900 mb-3">📅 Paiements Mensuels Récurrents</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-medium text-green-800">Montant mensuel (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={contractTreasuryData.paiement_mensuel}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        paiement_mensuel: parseFloat(e.target.value) || 0
                      })}
                      placeholder="0.00"
                      className="bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="font-medium text-green-800">Durée (mois)</Label>
                    <Input
                      type="number"
                      value={contractTreasuryData.duree_mois || 12}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        duree_mois: parseInt(e.target.value) || 12
                      })}
                      placeholder="12"
                      className="bg-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="font-medium text-green-800">Date début</Label>
                    <Input
                      type="date"
                      value={contractTreasuryData.date_debut_mensuel || ''}
                      onChange={(e) => setContractTreasuryData({
                        ...contractTreasuryData,
                        date_debut_mensuel: e.target.value
                      })}
                      className="bg-white"
                    />
                  </div>
                </div>
                
                <div className="mt-3 text-sm text-green-700">
                  Total mensuel sur {contractTreasuryData.duree_mois || 12} mois: {" "}
                  <span className="font-semibold">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12))}
                  </span>
                </div>
              </div>

              {/* Récapitulatif */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">📊 Récapitulatif Configuration</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avance initiale:</span>
                    <span className="font-medium">{new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format(contractTreasuryData.montant_avance || 0)}</span>
                  </div>
                  {contractTreasuryData.montant_deuxieme_tranche > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deuxième tranche:</span>
                      <span className="font-medium">{new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format(contractTreasuryData.montant_deuxieme_tranche || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paiements mensuels ({contractTreasuryData.duree_mois || 12} mois):</span>
                    <span className="font-medium">{new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12))}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-lg font-bold">
                    <span>TOTAL:</span>
                    <span className="text-blue-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'EUR'
                      }).format(
                        (contractTreasuryData.montant_avance || 0) + 
                        (contractTreasuryData.montant_deuxieme_tranche || 0) +
                        ((contractTreasuryData.paiement_mensuel || 0) * (contractTreasuryData.duree_mois || 12))
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowContractTreasuryModal(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      
                      // Nettoyer et valider les données
                      const cleanData = {
                        montant_avance: parseFloat(contractTreasuryData.montant_avance) || 0,
                        date_reglement_avance: contractTreasuryData.date_reglement_avance || '',
                        avance_status: contractTreasuryData.avance_status || 'pending',
                        montant_deuxieme_tranche: parseFloat(contractTreasuryData.montant_deuxieme_tranche) || 0,
                        date_reglement_deuxieme_tranche: contractTreasuryData.date_reglement_deuxieme_tranche || '',
                        deuxieme_tranche_status: contractTreasuryData.deuxieme_tranche_status || 'pending',
                        paiement_mensuel: parseFloat(contractTreasuryData.paiement_mensuel) || 0,
                        duree_mois: parseInt(contractTreasuryData.duree_mois) || 12,
                        date_debut_mensuel: contractTreasuryData.date_debut_mensuel || ''
                      };
                      
                      console.log("Sending treasury data:", cleanData);
                      await axios.put(`${API}/contracts/${selectedContractForTreasury.id}/treasury`, cleanData, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success("Configuration trésorerie sauvegardée");
                      setShowContractTreasuryModal(false);
                      fetchClientContracts(); // Rafraîchir les contrats
                    } catch (error) {
                      console.error("Error saving treasury config:", error);
                      console.error("Error response:", error.response?.data);
                      toast.error(`Erreur lors de la sauvegarde: ${error.response?.data?.detail || error.message}`);
                    }
                  }}
                >
                  Sauvegarder Configuration
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetails;